import { useEffect, useState } from 'react';
import { api, type RunSummary, type StepRunSummary, type WorkflowSummary } from '../api.ts';
import { RunTimeline } from '../components/RunTimeline.tsx';
import { StepChat } from '../components/StepChat.tsx';
import { WorkspaceChangesCard } from '../components/WorkspaceChangesCard.tsx';

/**
 * Runs and the per-step conversations behind them.
 *
 * Forking lives here rather than in the builder because a fork is a fact about
 * a *run*, not about a workflow: you keep everything the step had learned up to
 * that point and change what happens next.
 */
export function RunsView({ workflows }: { workflows: WorkflowSummary[] }) {
  const [workflowId, setWorkflowId] = useState<string | null>(workflows[0]?.id ?? null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepRunSummary[]>([]);
  const [selectedStep, setSelectedStep] = useState<StepRunSummary | null>(null);
  const [forking, setForking] = useState(false);
  const [effect, setEffect] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * Both loads below drop their result if the selection moved on while they were
   * in flight. Two requests started a moment apart do not have to land in that
   * order, and the failure is a quiet one: pick a workflow with no runs, then
   * pick one with forty, and the slower empty response paints "No runs yet" over
   * a list that had arrived. Nothing looks broken — the runs are simply not
   * there until something else triggers a reload.
   */
  useEffect(() => {
    if (!workflowId) return;
    let current = true;
    api
      .listRuns(workflowId)
      .then((result) => {
        if (!current) return;
        setRuns(result.runs);
        setRunId(result.runs[0]?.id ?? null);
      })
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [workflowId]);

  useEffect(() => {
    if (!runId) {
      setSteps([]);
      setSelectedStep(null);
      return;
    }
    let current = true;
    api
      .getRun(runId)
      .then((result) => {
        if (!current) return;
        setSteps(result.steps);
        setSelectedStep(result.steps[0] ?? null);
      })
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [runId]);

  const runStatus = runs.find((run) => run.id === runId)?.status ?? 'unknown';

  const control = async (intent: 'cancel' | 'pause' | 'resume') => {
    if (!runId) return;
    setError(null);
    try {
      const result = await api.controlRun(runId, intent);
      setEffect(result.effect);
      const refreshed = await api.listRuns(workflowId!);
      setRuns(refreshed.runs);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  const fork = async (step: StepRunSummary, message: string) => {
    if (!runId) return;
    setForking(true);
    try {
      const result = await api.forkRun(runId, { stepId: step.stepId, message, scope: 'subtree' });
      const refreshed = await api.listRuns(workflowId!);
      setRuns(refreshed.runs);
      setRunId(result.runId);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setForking(false);
    }
  };

  return (
    <div className={`body ${selectedStep ? 'with-inspector' : ''}`}>
      <aside className="sidebar">
        <p className="section-title">Workflow</p>
        <select value={workflowId ?? ''} onChange={(event) => setWorkflowId(event.target.value)}>
          {workflows.map((workflow) => (
            <option key={workflow.id} value={workflow.id}>
              {workflow.name}
            </option>
          ))}
        </select>

        <p className="section-title" style={{ marginTop: '1rem' }}>
          Runs
        </p>
        {runs.length === 0 && <p className="hint">No runs yet.</p>}
        {/*
          A run's headline is what started it, not what became of it. Leading
          every row with a filled status chip made a list of forty runs read as
          forty alerts; the dot carries the same fact in the margin, and the
          status stays as a word on the second line where it belongs.
        */}
        {runs.map((run) => (
          <button
            key={run.id}
            className="list-item run-row"
            aria-current={run.id === runId}
            onClick={() => setRunId(run.id)}
          >
            <span className={`run-dot ${statusTone(run.status)}`} aria-hidden="true" />
            <span className="run-row-body">
              <span className="run-row-head">
                {run.triggerType}
                {run.forkedFromRun && <span className="run-tag">fork</span>}
              </span>
              <small>
                {run.status} · {relativeTime(run.createdAt)}
              </small>
            </span>
          </button>
        ))}
      </aside>

      <main className="main">
        {error && (
          <div className="banner danger">
            <h3>Something went wrong</h3>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {!runId && <div className="empty">Pick a run to see its steps.</div>}

        {runId && (
          <>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: '0 0 0.25rem' }}>Run <code>{runId}</code></h1>
                <p className="hint" style={{ marginTop: 0 }}>
                  Each step is its own durable conversation. Open one to keep talking to it — days
                  later, if you like.
                </p>
              </div>
              {LIVE.has(runStatus) && (
                <>
                  <button onClick={() => void control(runStatus === 'paused' ? 'resume' : 'pause')}>
                    {runStatus === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                  <button className="ghost danger" onClick={() => void control('cancel')}>
                    Cancel
                  </button>
                </>
              )}
            </div>

            {/*
              What the control did, in its own words. Cancelling is the one that
              takes a moment — a step mid-call finishes it first — so the button
              reports what will happen rather than the screen asserting that it
              already has.
            */}
            {effect && (
              <div className="banner warn">
                <p style={{ margin: 0 }}>{effect}</p>
              </div>
            )}

            {/*
              The timeline above the table, because they answer different
              questions and the live one is asked first: the table says what the
              run is made of, and this says what it is doing — including the
              calls that were refused, which is the fact nothing else on this
              screen carries.
            */}
            <RunTimeline runId={runId} />

            {/* What the run changed on disk, which the timeline cannot say. Renders nothing for a run that never wrote a file. */}
            <WorkspaceChangesCard runId={runId} />

            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Status</th>
                    <th>Spend</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step) => (
                    <tr key={step.conversationId}>
                      <td>
                        <strong>{step.stepId}</strong>
                        {step.error && (
                          <div style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>{step.error}</div>
                        )}
                      </td>
                      <td>
                        <span className={`pill ${statusTone(step.status)}`}>{step.status}</span>
                      </td>
                      <td>${(step.spendCents / 100).toFixed(2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="ghost" onClick={() => setSelectedStep(step)}>
                          Open chat
                        </button>
                      </td>
                    </tr>
                  ))}
                  {steps.length === 0 && (
                    <tr>
                      <td colSpan={4} className="hint">
                        This run has not started any steps yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {selectedStep && (
        <aside className="inspector">
          <StepChat
            conversationId={selectedStep.conversationId}
            stepName={selectedStep.stepId}
            onFork={() => {
              const message = window.prompt(
                `Fork the run from "${selectedStep.stepId}".\n\nThe new run keeps everything this step had established. What should it do differently?`,
                '',
              );
              if (message !== null) void fork(selectedStep, message);
            }}
          />
          {forking && <p className="hint">Forking…</p>}
        </aside>
      )}
    </div>
  );
}

/** Statuses a run can still be asked to leave. Mirrors `LIVE_RUN_STATUSES`. */
const LIVE = new Set(['pending', 'running', 'awaiting_approval', 'paused']);

function statusTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'running') return 'accent';
  return 'warn';
}

/**
 * How long ago, in as few characters as it takes.
 *
 * A run list is scanned for recency, and `8/18/2026, 3:41:23 AM` makes you do
 * the arithmetic yourself on every row. Anything older than a week is a date
 * again, because "9d ago" stops being the useful framing at about that point.
 */
function relativeTime(iso: string): string {
  const then = new Date(iso);
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
